<template>
    <b-modal v-model="modal_raw_material.show"
             centered
             :title="getTitle()"
             size="xl"
             hide-footer
             hide-header-close
             dialog-class="calculator-dialog"
    >
        <b-form @submit.prevent="validateForm"
                data-vv-scope="raw_material"
                autocomplete="off"
        >
            <h5 class="text-primary">Matería prima</h5>

            <b-row>
                <b-col md="9">
                    <b-form-group label="Nombre"
                                  label-for="name"
                                  class="mb-0"
                    >
                        <el-input id="name"
                                  name="name"
                                  v-model="raw_material.name"
                                  size="small"
                                  placeholder="Ingrese el nombre"
                                  data-vv-as=" "
                                  v-validate="'required|min:3|max:255'"
                                  class="text-uppercase-input"
                        />
                        <span class="text-danger span-error small"
                              v-text="errors.first('raw_material.name')"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="% Usado en la preparación"
                                  label-for="material-percentage"
                                  class="mb-0"
                    >
                        <input_formatter id="percentage"
                                         name="percentage"
                                         v-model="raw_material.percentage"
                                         symbol="%"
                                         :position_symbol="false"
                                         :decimals="4"
                                         input_class="text-center"
                                         data-vv-as=" "
                                         v-validate="'required|min_value:0.001|max_value:100'"
                        />
                        <span class="text-danger span-error small"
                              v-text="errors.first('raw_material.percentage')"
                        />
                    </b-form-group>
                </b-col>
                <b-col cols="12">
                    <hr class="my-3">
                </b-col>
            </b-row>

            <h5 class="text-primary">
                <i class="fa fa-search"/>
                Buscar componentes
            </h5>

            <b-row>
                <b-col md="5">
                    <b-form-group label=" "
                                  class="mb-0"
                    >
                        <el-input size="small"
                                  id="component-search"
                                  v-model="filter"
                                  placeholder="Buscar por nombre o CAS"
                                  @keyup.enter="getComponents(filter)"
                                  class="text-uppercase-input"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="7">
                    <b-form-group label=" "
                                  class="mb-0"
                    >
                        <b-button @click="_getComponents(filter)"
                                  variant="primary"
                                  size="sm"
                        >
                            <i class="fa fa-search"/>
                            Buscar
                        </b-button>
                        <!-- Botón para crear nuevo componente -->
                        <b-button @click="openModalComponentToCreation"
                                  variant="success"
                                  size="sm"
                                  class="pull-right"
                        >
                            <i class="fa fa-plus"/> Nuevo Componente
                        </b-button>
                    </b-form-group>
                </b-col>

                <b-col cols="12">
                    <hr class="my-3">
                </b-col>
            </b-row>

            <h5 class="text-primary">
                <i class="fa fa-cubes"/>
                Componentes de la materia prima

                <b-badge :variant="raw_material.components.length ? 'success' : 'danger'">
                    {{ raw_material.components.length }}
                </b-badge>
            </h5>

            <b-table :items="raw_material.components"
                     :fields="columns"
                     striped
                     small
                     responsive
                     hover
                     empty-text="No hay componentes agregados a la materia prima"
                     show-empty
                     class="mb-1"
            >
                <template v-slot:cell(name)="data">
                    <ins class="text-primary cursor-pointer"
                         @click="openModalComponentToEdition(data.item)"
                    >
                        {{ data.item.name.toUpperCase() }}
                    </ins>
                    <b-badge :id="'popover-badge-c' + data.index"
                             variant="danger"
                             class="ml-1 cursor-pointer"
                    >
                        {{ data.item.sga_categories.length }}
                    </b-badge>
                    <b-popover :target="'popover-badge-c' + data.index"
                               triggers="hover"
                               placement="top"
                               :key="'popover-c' + data.index"
                               custom-class="custom-popover"
                    >
                        <template #title>
                            Clasificación SGA
                        </template>
                        <div>
                            <div v-for="(item, idx) in data.item.sga_categories"
                                 :key="idx"
                                 class="text-truncate"
                            >
                                {{ item.category.codigo }} - {{ item.category.categoria }}
                            </div>
                        </div>
                    </b-popover>
                </template>

                <template v-slot:cell(reported_percentage)="data">
                    <input_formatter v-model="data.item.reported_percentage"
                                     symbol="%"
                                     :position_symbol="false"
                                     :decimals="4"
                                     input_class="text-center"
                    />
                </template>

                <template v-slot:cell(omit_in_composition)="data">
                    <div class="text-center">
                        <b-form-checkbox v-model="data.item.omit_in_composition"
                                         :unchecked-value="0"
                                         :value="1"
                        />
                    </div>
                </template>

                <template v-slot:cell(omit_in_carcinogens)="data">
                    <div class="text-center">
                        <b-form-checkbox v-model="data.item.omit_in_carcinogens"
                                         :unchecked-value="0"
                                         :value="1"
                        />
                    </div>
                </template>

                <template v-slot:cell(final_percentage)="data">
                    {{ calculateFinalPercentage(data.item).toFixed(2) }}%
                </template>

                <template #cell(actions)="data">
                    <b-button size="sm"
                              variant="danger"
                              @click="removeComponent(data.item, data.index)"
                              class="float-right mr-1"
                    >
                        <i class="fa fa-trash"/>
                        Eliminar
                    </b-button>
                </template>
            </b-table>

            <b-row>
                <b-col>
                    <span class="text-danger span-error small"
                          v-text="errors.first('raw_material.components')"
                    />
                </b-col>
            </b-row>

            <b-row class="mt-2">
                <b-col class="text-center">
                    <b-button variant="primary"
                              size="sm"
                              class="mx-1"
                              @click="validateForm"
                    >
                        <i class="fa fa-save"/>
                        Guardar
                    </b-button>
                    <b-button variant="secondary"
                              size="sm"
                              class="mx-1"
                              @click="setShowToModalRawMaterial(false)"
                    >
                        <i class="fa fa-close"/>
                        Cancelar
                    </b-button>
                </b-col>
            </b-row>
        </b-form>

        <components_list v-if="show_results"
                         :show_results.sync="show_results"
                         @closeResults="show_results = false"
                         @selectResult="selectComponent"
        />
    </b-modal>
</template>

<script>

import {mapActions, mapMutations, mapState} from 'vuex';
import input_formatter from '../../components/input_formatter.vue';
import components_list from './components_list.vue';
import Swal from 'sweetalert2';

export default {
    name: 'RawMaterialForm',
    components: {
        input_formatter,
        components_list
    },
    data() {
        return {
            material: {
                name: '',
                percentage: null,
                components: []
            },
            filter: '',
            searchResults: [],
            component: {
                name: '',
                cas: '',
                hPhrases: []
            },
            show_results: false,
            columns: [
                {
                    key: 'cas_number',
                    label: 'No. CAS',
                    thStyle: 'width: 120px; max-width: 120px;'
                },
                {
                    key: 'name',
                    label: 'Nombre'
                },
                {
                    key: 'reported_percentage',
                    label: '% Máx reportado en la FDS de la materia prima',
                    thClass: 'text-center',
                    thStyle: 'width: 140px; max-width: 140px;'
                },
                {
                    key: 'final_percentage',
                    label: '% Final',
                    tdClass: 'text-center',
                    thClass: 'text-center',
                    thStyle: 'width: 120px; max-width: 120px;'
                },
                {
                    key: 'omit_in_composition',
                    label: 'Omitir de Composición',
                    thClass: 'text-center',
                    thStyle: 'width: 110px; max-width: 110px;'
                },
                {
                    key: 'omit_in_carcinogens',
                    label: 'Omitir de Cancerígenos',
                    thClass: 'text-center',
                    thStyle: 'width: 110px; max-width: 110px;'
                },
                {
                    key: 'actions',
                    label: '',
                    thStyle: 'width: 100px; max-width: 100px;'
                }
            ]
        };
    },
    computed: {
        ...mapState('calculator', [
            'product',
            'components',
            'raw_material',
            'modal_raw_material'
        ])
    },
    methods: {
        ...mapActions('calculator', [
            'getComponents',
            'addRawMaterial',
            'createRawMaterial',
            'updateRawMaterial',
            'getProduct'
        ]),
        ...mapMutations('calculator', [
            'setComponents',
            'addComponentToRawMaterial',
            'deleteComponentToRawMaterial',
            'setShowToModalRawMaterial',
            'setModalComponentShow',
            'resetComponent',
            'setActionToModalComponent',
            'setComponent'
        ]),
        async _getComponents(filter) {
            await this.getComponents(filter);
            this.show_results = true;
        },
        getTitle() {
            switch (this.modal_raw_material.action) {
                case 'c':
                    return 'Crear matería prima';
                case 'a':
                    return 'Agregar matería prima';
                case 'u':
                    return 'Actualizar matería prima';
                default:
                    return 'Crear matería prima';
            }
        },
        async validateForm() {
            if (!this.raw_material.components.length) {
                this.$validator.errors.add({
                    field: 'components',
                    msg: 'Debe agregar por lo menos un componente',
                    rule: 'manual',
                    scope: 'raw_material'
                });

                return;
            } else {
                this.$validator.errors.remove('raw_material.components');
            }

            await this.$validator.validateAll('raw_material')
                .then(async result => {
                    if (result) {
                        if (this.modal_raw_material.action === 'c') {
                            await this.createRawMaterial();
                        } else if (this.modal_raw_material.action === 'a') {
                            await this.addRawMaterial();
                        } else {
                            await this.updateRawMaterial();
                        }

                        this.setShowToModalRawMaterial(false);
                        const product_id = this.$route.params.product_id;
                        const company_id = this.$route.params.company_id;

                        if (!!product_id && !!company_id) {
                            await this.getProduct({product_id, company_id});
                        }
                    }
                });
        },
        selectComponent(component) {
            this.addComponentToRawMaterial(component);
            this.setComponents([]);
            this.filter = '';
            this.show_results = false;
        },
        openModalComponentToEdition(component) {
            let comp = JSON.parse(JSON.stringify(component));

            this.setComponent(comp);
            this.setActionToModalComponent('u');
            this.setShowToModalRawMaterial(false);
            this.setModalComponentShow(true);
        },
        openModalComponentToCreation() {
            this.resetComponent();
            this.setActionToModalComponent('c');
            this.setShowToModalRawMaterial(false);
            this.setModalComponentShow(true);
        },
        async removeComponent(c, index) {
            await Swal.fire({
                title: 'Eliminar',
                text: `Está seguro de eliminar el componente con el No. de CAS ${c.cas_number}?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                cancelButtonText: 'Cancelar',
                confirmButtonText: 'Si, eliminar'
            }).then(async (result) => {
                if (result.isConfirmed) {
                    this.deleteComponentToRawMaterial(index);
                }
            });
        },
        calculateFinalPercentage(component) {
            let percentage = this.product.total_percentage;

            if (!percentage) {
                percentage = this.raw_material.percentage;
            }

            if (!percentage) return 0;

            return this.raw_material.percentage * component.reported_percentage / percentage;
        }
    }
};
</script>

<style>
.custom-popover {
    max-width: 800px;
}

/* Transiciones para resultados */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s, transform 0.3s;
}

.fade-leave-to, .fade-enter {
    opacity: 0;
    transform: translate(-50%, -45%);
}

.text-uppercase-input input {
    text-transform: uppercase;
}
</style>
